/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createAsyncThunk } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ConvoSkill } from '../../../../ts/convo-skill';
import { CreateAsyncThunk } from '../../../../ts/state';
import { selectActionSkillId } from '../../../ia/store/assistants/core-selectors';
import { selectDraftEnvironmentId } from '../../../ia/store/environments/selectors';
import { addErrorMessage } from '../../../ia/store/messages/actions';
import { getAssistantId } from '../../../ia/store/session/selectors';
import * as api from '../../api/convo-skills';
import {
  listEnabledSystemEntities,
  listSystemEntities
} from '../../api/entities/entities';
import { ConvoSkillDraft } from '../../hooks/useConvoSkillDraft';
import { listCollections } from '../collections/actions';
import {
  generateCreateConvoSkillBody,
  generateUpdateConvoSkillBody,
  mapSlotsAndVariables
} from './util';

export interface TransactionUpdateResponse {
  updated: string;
}

export const listConvoSkills = createAsyncThunk<
  ConvoSkill[],
  void,
  CreateAsyncThunk
>('convoSkills/list', async (_, { dispatch, getState }) => {
  try {
    const assistant_id = getAssistantId(getState());
    const environment_id = selectDraftEnvironmentId(getState());

    const response = await api.listConvoSkills({
      assistant_id,
      environment_id
    });
    const retVal = response.conversational_skills ?? [];
    return retVal;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const getConvoSkill = createAsyncThunk<
  ConvoSkill,
  { skillDraft: ConvoSkillDraft; actionId: string },
  CreateAsyncThunk
>('convoSkills/get', async (params, { dispatch, getState }) => {
  try {
    const { skillDraft } = params;

    const assistant_id = getAssistantId(getState());
    const environment_id = selectDraftEnvironmentId(getState());

    const response = await api.getConvoSkill(
      {
        assistant_id,
        environment_id,
        provider_id: decodeURIComponent(skillDraft.providerId)
      },
      skillDraft.convoSkillId
    );
    return response.data;
  } catch (error) {
    dispatch(addErrorMessage(error));
    throw error;
  }
});

export const createConvoSkillAction = createAsyncThunk<
  TransactionUpdateResponse,
  { skillDraft: ConvoSkillDraft; actionId: string },
  CreateAsyncThunk
>('convoSkills/create', async (params, { dispatch, getState }) => {
  try {
    const { skillDraft, actionId } = params;
    const draftCopy = _.clone(skillDraft);
    // use an optionally supplied actionId only if none present on draft
    if (!draftCopy.actionId) {
      draftCopy.actionId = actionId;
    }
    const assistantId = getAssistantId(getState());
    const environmentId = selectDraftEnvironmentId(getState());
    const actionSkillId = selectActionSkillId(getState());

    const [allSystemEntities, enabledSysEntities] = await Promise.all([
      listSystemEntities(actionSkillId, true, assistantId, false),
      listEnabledSystemEntities(actionSkillId, assistantId, false)
    ]);

    let resp: api.GetConvoSkillResponseData;
    try {
      resp = await api.getConvoSkill(
        {
          assistant_id: assistantId,
          environment_id: environmentId,
          provider_id: decodeURIComponent(skillDraft.providerId)
        },
        skillDraft.convoSkillId
      ); // TODO: move it to before user click next button
    } catch (error) {
      console.log('getConvoSkill action error:', error);
    }

    const { slots, variables } = mapSlotsAndVariables(resp?.data);
    const createBody = generateCreateConvoSkillBody(
      draftCopy,
      enabledSysEntities.entities,
      allSystemEntities.entities,
      slots,
      variables
    );
    const response = await api.createConvoSkill(
      assistantId,
      actionSkillId,
      createBody
    );
    await dispatch(listCollections());
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    console.log('createConvoSkill action error:', error);
    throw error;
  }
});

export const updateConvoSkillAction = createAsyncThunk<
  TransactionUpdateResponse,
  { skillDraft: ConvoSkillDraft },
  CreateAsyncThunk
>('convoSkills/update', async (params, { dispatch, getState }) => {
  try {
    const { skillDraft } = params;

    const assistantId = getAssistantId(getState());
    const actionSkillId = selectActionSkillId(getState());
    const updateBody = generateUpdateConvoSkillBody(skillDraft);
    const response = await api.updateConvoSkill(
      assistantId,
      actionSkillId,
      updateBody
    );
    return response;
  } catch (error) {
    dispatch(addErrorMessage(error));
    console.log('updateConvoSkill action error:', error);
    throw error;
  }
});
